<template>
  <div class="flex flex-col max-w-lg grow text-center self-center">
    <h1 class="text-2xl sm:text-3xl font-bold text-center sm:px-20 mb-4">
      {{ $t(`${trPrefix}check`) }}
    </h1>

    <p class="text-pretty mb-6">
      {{ $t(`${trPrefix}checkDescription`) }}
    </p>

    <div
      class="border border-bgr-300 rounded p-6 pb-8 flex flex-col items-start"
    >
      <span class="mb-6 text-start text-pretty">
        <span>
          {{ $t(`${trPrefix}weSent`) }}
        </span>

        &nbsp; <b>{{ email }}</b
        >.
      </span>
      <WebccFeedbackMessage
        v-if="errorMessage"
        type="error"
        :message="errorMessage"
        class="mb-6 w-full"
      />
      <WebccFeedbackMessage
        v-if="infoMessage"
        type="info"
        :message="infoMessage"
        class="mb-6 w-full"
      />
      <!-- Code input to enter the code from the email -->
      <WebccOneTimePassword
        v-if="!loading"
        :initial-code="initialCode"
        :invalid="inputsInvalid"
        class="mb-8"
        @code-entered="submit"
        @change="updateCode"
      />
      <WebccLoaderOverlay v-else size="sm" absolute />
      <WebccButton
        class="mb-2 w-full"
        :variant="loading ? 'white' : 'theme'"
        :disabled="loading"
        outline
        size="lg"
        @click="submit(currentCode)"
      >
        {{ $t(`${trPrefix}${loading ? 'confirmProgress' : 'confirm'}`) }}
      </WebccButton>
      <UserAgreement />
      <hr class="border-bgr-200 my-6 w-full" />

      <span>
        {{ $t(`${trPrefix}noEmail`) }}
      </span>

      <span class="flex mb-4 text-txt-stronger text-sm"
        >(

        <span>
          {{ $t(`${trPrefix}checkSpam`) }}
        </span>

        )</span
      >
      <button
        class="text-txt-strongest underline inline-block hover:cursor-pointer duration-100 hover:text-cta disabled:opacity-10 disabled:hover:cursor-not-allowed disabled:hover:text-txt-strongest disabled:no-underline"
        :disabled="resendTimer !== 0"
        @click="resendCode"
      >
        {{ $t(`${trPrefix}resend`) }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
const trPrefix = 'components.auth.register.registerCodeVerification.'
const CODE_LENGTH = 6

defineProps<{
  code?: string
  emits?: boolean
}>()

const emit = defineEmits<{
  verified: []
  isReady: []
}>()

const { t } = useI18n()
const { getErrorMessage } = useAuthError(trPrefix)
const auth = useAuthStore()
const authModalEvt = useEventBus<AuthState>('authModal')
const toaster = useToaster()
const tracking = useTracking()

const email = ref<string | undefined>(auth.email)
const initialCode = ref()
const infoMessage = ref<string>()
const errorMessage = ref<string>()
const resendTimer = ref(0)
const currentCode = ref('')
const loading = ref(false)

await initRegistrationVerification()

onMounted(() => {
  emit('isReady')
})

onUnmounted(() => {
  errorMessage.value = undefined
  loading.value = false
})

async function initRegistrationVerification() {
  if (!import.meta.client) return
  if (auth.isRegistrationStarted) return

  await triggerVerification()
}

const inputsInvalid = computed(() => errorMessage.value !== undefined)

function updateCode(value: string) {
  currentCode.value = value
  if (currentCode.value.length === CODE_LENGTH) {
    submit(currentCode.value)
  }
}

async function triggerVerification() {
  errorMessage.value = undefined

  try {
    await auth.verifyStart()
    await auth.verifyComplete({ email: email.value })
  } catch (error) {
    errorMessage.value = getErrorMessage(error)
  }
}

async function submit(code: string) {
  errorMessage.value = undefined
  initialCode.value = undefined
  loading.value = true

  try {
    await auth.verifyComplete({ code })

    tracking.handlers?.auth.verify()
    authModalEvt.emit({ status: false })
  } catch (error) {
    errorMessage.value = getErrorMessage(error)
  } finally {
    loading.value = false
  }
}

/**
 * Resends the code and set the timer to 60 seconds.
 */
async function resendCode() {
  errorMessage.value = t(`${trPrefix}checkSpamInfo`)
  resendTimer.value = 60
  const interval = setInterval(() => {
    resendTimer.value--
    if (!resendTimer.value) {
      clearInterval(interval)
    }
  }, 1000)

  await triggerVerification()

  toaster.success(t(`${trPrefix}codeSent`))
}
</script>
